import Vue from 'vue';
import mixins from 'vue-typed-mixins';
import dayjs from 'dayjs';
import { AxiosResponse } from 'axios';
import { ElUploadInternalFileDetail } from 'element-ui/types/upload';
import { ErrorResponse, FieldErrorResponse, FileExportMixin, HttpStatusCode } from '@codequality/vue-cli-base';

import { E勘定科目カテゴリ, E取込データ種別, E取込分類, E取込形式, E帳票種別, E補助科目フラグ } from '@/openapi/enum';
import {
  勘定科目Api,
  勘定科目ListItemResponse,
  取込Api,
  取込FileListItemResponse,
  取込PdfListItemResponse,
  取込Pdf決算書ListItemRequest,
  取込Pdf試算表ListItemRequest,
  帳票Api,
  帳票ListItemRequest,
} from '@/openapi/user';

import { 取込Error, 取込データ種別Error } from '@/components/取込/error/取込Error';

import TabsMixin from '@/mixins/TabsMixin';
import 計画書Mixin from '@/entries/user/mixins/計画書Mixin';
import 事業所Mixin from '@/entries/user/mixins/事業所Mixin';

import UserApiUtility from '@/classes/common/UserApiUtility';
import { DAYJS_Y_NUMBER_FORMAT, 大分類_販売管理費 } from '@/classes/common/Constants';

export type ImportInfo = {
  取込形式: E取込形式;
  取込分類: E取込分類;
  年: number;
  部門pk?: number;
  file?: File;
  pdf取込データ種別s: E取込データ種別[];
};

export type Default分類 = {
  大分類: string;
  中分類?: string;
};

type State = {
  importInfo: ImportInfo;
  勘定科目s: 勘定科目ListItemResponse[];
};

const state = Vue.observable<State>({
  importInfo: {
    取込形式: E取込形式.pdf,
    取込分類: E取込分類.決算書,
    年: Number(dayjs().format(DAYJS_Y_NUMBER_FORMAT)),
    pdf取込データ種別s: [],
  },
  勘定科目s: [],
});

export default mixins(FileExportMixin, TabsMixin, 計画書Mixin, 事業所Mixin).extend({
  name: 'M取込Mixin',

  data(): {
    取込データs: 取込PdfListItemResponse[];
    取込データ種別Errors: 取込データ種別Error[];
    readonly outputConfig: {
      template: {
        header: number;
        row: number;
      };
      column: {
        勘定科目: number;
        value: number;
      };
    };
  } {
    return {
      取込データ種別Errors: [],
      取込データs: [],
      outputConfig: {
        template: {
          header: 0,
          row: 1,
        },
        column: {
          勘定科目: 0,
          value: 1,
        },
      },
    };
  },

  computed: {
    取込title(): string {
      const fileName = state.importInfo.file?.name;
      return fileName !== undefined ? `${fileName} 取込データ選択` : '';
    },
    importInfo(): ImportInfo {
      return state.importInfo;
    },
    勘定科目s(): 勘定科目ListItemResponse[] {
      return state.勘定科目s;
    },
  },

  filters: {
    取込分類Label(text: string): string {
      return text
        .replace('_', '・')
        .replace(/三表計画/g, '')
        .replace(/実績/g, '');
    },
  },

  methods: {
    async load取込データs(sj: string): Promise<boolean> {
      const options = {
        params: {
          sj,
        },
      };
      const responses = await Promise.all([
        UserApiUtility.ogApi(勘定科目Api).list勘定科目(this.事業所.pk, E補助科目フラグ.通常),
        UserApiUtility.ogApi(取込Api).list取込Pdfデータ(
          this.計画書.pk,
          this.importInfo.取込分類,
          this.importInfo.年,
          this.importInfo.pdf取込データ種別s,
          this.importInfo.部門pk,
          options
        ),
      ]);

      // list取込PdfデータがbadRequestの場合はalert表示してtrueを返却
      if (responses[1].status === HttpStatusCode.badRequest) {
        alert('データ取込に失敗しました');
        return true;
      }

      state.勘定科目s = responses[0].data;
      this.取込データs = responses[1].data;
      return false;
    },
    setImportInfo(取込形式: E取込形式, 取込分類: E取込分類, 決算年: number, 部門pk?: number): void {
      state.importInfo = {
        取込形式: 取込形式,
        取込分類: 取込分類,
        年: 決算年,
        部門pk: 部門pk,
        pdf取込データ種別s: [],
      };
    },
    selectImportFile(取込形式: E取込形式, file: ElUploadInternalFileDetail): boolean {
      // ファイルタイプチェック
      let isValidExt = false;
      switch (file.raw.type) {
        case 'application/pdf':
          isValidExt = 取込形式 === E取込形式.pdf;
          break;
        case 'application/vnd.ms-excel':
        case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
          isValidExt = 取込形式 === E取込形式.xlsx;
          break;
      }
      if (!isValidExt) {
        alert(`${E取込形式[取込形式]}ファイルを選択してください`);
        return false;
      }

      this.$set(state.importInfo, 'file', file.raw);
      return true;
    },
    set取込データ種別s(取込データ種別s: E取込データ種別[]): void {
      state.importInfo.pdf取込データ種別s = 取込データ種別s;
    },
    set取込勘定科目(
      request: 取込Pdf決算書ListItemRequest | 取込Pdf試算表ListItemRequest,
      勘定科目: 勘定科目ListItemResponse | undefined,
      item: 取込FileListItemResponse,
      default分類: Default分類
    ): void {
      request.勘定科目Pk = 勘定科目?.pk;
      request.取込勘定科目 = {
        c大分類: 勘定科目 !== undefined ? 勘定科目.c大分類 : default分類.大分類,
        c中分類: 勘定科目 !== undefined ? 勘定科目.c中分類 : default分類.中分類,

        c勘定科目: this.get取込勘定科目(item),
      };

      default分類.大分類 = request.取込勘定科目!.c大分類;
      default分類.中分類 = request.取込勘定科目!.c中分類;
    },
    getCカテゴリFrom取込データ種別(取込データ種別: E取込データ種別): E勘定科目カテゴリ {
      switch (取込データ種別) {
        case E取込データ種別.貸借対照表勘定式:
        case E取込データ種別.貸借対照表報告式:
        case E取込データ種別.固定資産台帳:
        case E取込データ種別.月次貸借対照表:
          return E勘定科目カテゴリ.貸借;
        case E取込データ種別.損益計算書:
        case E取込データ種別.販売費および一般管理費内訳書:
        case E取込データ種別.月次損益計算書:
          return E勘定科目カテゴリ.損益;
        case E取込データ種別.製造原価報告書:
        case E取込データ種別.月次製造原価報告書:
          return E勘定科目カテゴリ.製造原価;
        default:
          throw new Error(`illeagal E取込データ種別: ${取込データ種別}`);
      }
    },
    get取込データ種別Label(種別: E取込データ種別): string {
      switch (種別) {
        case E取込データ種別.貸借対照表勘定式:
        case E取込データ種別.貸借対照表報告式:
          return '貸借対照表';
        default:
          return E取込データ種別[種別];
      }
    },
    getDefault分類(取込データ種別: E取込データ種別): Default分類 {
      const result: Default分類 = { 大分類: '' };

      switch (取込データ種別) {
        case E取込データ種別.貸借対照表勘定式:
        case E取込データ種別.貸借対照表報告式:
        case E取込データ種別.月次貸借対照表:
          result.大分類 = '流動資産';
          result.中分類 = '現金及び預金';
          break;
        case E取込データ種別.損益計算書:
        case E取込データ種別.月次損益計算書:
          result.大分類 = '売上高';
          break;
        case E取込データ種別.販売費および一般管理費内訳書:
          result.大分類 = 大分類_販売管理費;
          break;
        case E取込データ種別.製造原価報告書:
        case E取込データ種別.月次製造原価報告書:
          result.大分類 = '材料費';
          result.中分類 = '期首材料棚卸高';
          break;
      }

      return result;
    },
    get取込勘定科目(item: 取込FileListItemResponse): string {
      return item.勘定科目 ?? '';
    },
    get取込Error(e: 取込Error): string {
      return `${e.index !== undefined ? e.index + 1 + '行目' : ''} ${e.field}：${e.message}`;
    },
    add取込Error(取込データ種別: E取込データ種別, field: string, message: string, index?: number): void {
      let 取込Error = this.取込データ種別Errors.find((e) => e.取込データ種別 === 取込データ種別);
      if (取込Error === undefined) {
        取込Error = {
          取込データ種別,
          errors: [],
        };
        this.取込データ種別Errors.push(取込Error);
      }

      取込Error.errors.push({
        index,
        field,
        message,
      });
    },
    filter勘定科目(種別: E取込データ種別): 勘定科目ListItemResponse[] {
      if (種別 === E取込データ種別.販売費および一般管理費内訳書) {
        return this.勘定科目s.filter((勘) => 勘.c大分類 === 大分類_販売管理費);
      }

      const カテゴリ = this.getCカテゴリFrom取込データ種別(種別);
      return this.勘定科目s.filter((勘) => 勘.cカテゴリ === カテゴリ);
    },
    is取込ValidationErrorResponse<T>(
      response: AxiosResponse<T | ErrorResponse>,
      set取込Errors?: (errors: FieldErrorResponse[]) => void
    ): boolean {
      if (response.status === 200) return false;

      if (response.status !== HttpStatusCode.badRequest) return true;

      if (set取込Errors === undefined) {
        set取込Errors = this.$_set取込ErrorsFromResponse;
      }
      set取込Errors((response.data as ErrorResponse).errors!);

      // エラー内容をindex、fieldの順でソート
      this.取込データ種別Errors.forEach((error) => {
        error.errors.sort((a, b) => {
          if (a.index !== undefined && b.index !== undefined) {
            if (a.index < b.index) return -1;
            if (a.index > b.index) return 1;
          }
          if (a.field < b.field) return -1;
          if (a.field > b.field) return 1;
          return 0;
        });
      });

      return true;
    },
    async outputXlsx<T>(tabs: T[], createRequest: (tab: T) => 帳票ListItemRequest): Promise<void> {
      const response = await UserApiUtility.ogApi(帳票Api).output帳票(
        E帳票種別.xlsx,
        tabs.map(createRequest),
        this.getExportOptions()
      );
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      this.exportFile(response, process.env.VUE_APP_IS_MOCK === 'true');
    },
    $_set取込ErrorsFromResponse(errors: FieldErrorResponse[]): void {
      // エラー内容がない場合はreturn
      if (errors.length === 0) return;

      // file自体のエラーの場合はその内容を表示してreturn
      if (errors[0].field === 'file') {
        this.add取込Error(E取込データ種別.取込なし, errors[0].field, errors[0].defaultMessage);
        return;
      }

      // 取込分類毎のエラー格納処理
      let e取込データ種別 = E取込データ種別.取込なし;
      switch (this.importInfo.取込分類) {
        case E取込分類.決算書:
        case E取込分類.月次貸借対照表:
        case E取込分類.月次損益計算書_月次製造原価報告書:
          errors.forEach((error) => {
            const fields = error.field.split('.');
            e取込データ種別 = Number(fields[0].match(/\d+/));
            const index = Number(fields[1].match(/\d+/));
            const field = fields[2];
            this.add取込Error(e取込データ種別, field, error.defaultMessage, index);
          });
          break;

        default:
          errors.forEach((error) => {
            const fields = error.field.split('.');
            const index = fields[0].match(/\d+/);
            const field = fields[1];
            this.add取込Error(e取込データ種別, field, error.defaultMessage, Number(index));
          });
          break;
      }
    },
  },
});
